import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const buttonVariants = {
  variant: {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
  },
  size: {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
  },
}

export const cardVariants = {
  variant: {
    default: 'rounded-lg border bg-card text-card-foreground shadow-sm',
    elevated: 'rounded-lg border bg-card text-card-foreground shadow-lg',
    outlined: 'rounded-lg border-2 bg-card text-card-foreground',
  },
  padding: {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  },
}

export const inputVariants = {
  variant: {
    default: 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
    error: 'flex h-10 w-full rounded-md border border-destructive bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  },
  size: {
    default: 'h-10 px-3 py-2',
    sm: 'h-9 px-3 py-2 text-sm',
    lg: 'h-11 px-4 py-2 text-base',
  },
}

export const selectVariants = {
  variant: {
    default: 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
    error: 'flex h-10 w-full rounded-md border border-destructive bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  },
  size: {
    default: 'h-10 px-3 py-2',
    sm: 'h-9 px-3 py-2 text-sm',
    lg: 'h-11 px-4 py-2 text-base',
  },
}

export const badgeVariants = {
  variant: {
    default: 'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
    secondary: 'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
    destructive: 'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
    outline: 'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground',
    success: 'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-success-500 text-white hover:bg-success-600',
    warning: 'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-warning-500 text-white hover:bg-warning-600',
  },
}

export const alertVariants = {
  variant: {
    default: 'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
    destructive: 'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive',
    success: 'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground border-success-500/50 text-success-700 dark:border-success-500 [&>svg]:text-success-500',
    warning: 'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground border-warning-500/50 text-warning-700 dark:border-warning-500 [&>svg]:text-warning-500',
  },
}

export const typographyVariants = {
  variant: {
    h1: 'scroll-m-20 text-5xl font-extrabold tracking-tight lg:text-6xl xl:text-7xl',
    h2: 'scroll-m-20 pb-2 text-4xl font-semibold tracking-tight lg:text-5xl first:mt-0',
    h3: 'scroll-m-20 text-3xl font-semibold tracking-tight lg:text-4xl',
    h4: 'scroll-m-20 text-2xl font-semibold tracking-tight lg:text-3xl',
    h5: 'scroll-m-20 text-xl font-semibold tracking-tight lg:text-2xl',
    h6: 'scroll-m-20 text-lg font-semibold tracking-tight lg:text-xl',
    p: 'leading-7 text-base lg:text-lg [&:not(:first-child)]:mt-6',
    lead: 'text-xl lg:text-2xl text-muted-foreground',
    large: 'text-lg lg:text-xl font-semibold',
    small: 'text-sm lg:text-base font-medium leading-none',
    muted: 'text-sm lg:text-base text-muted-foreground',
    code: 'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm lg:text-base font-semibold',
  },
}

export const breadcrumbVariants = {
  size: {
    sm: 'text-xs',
    default: 'text-sm',
    lg: 'text-base',
  },
  spacing: {
    sm: 'space-x-0.5',
    default: 'space-x-1',
    lg: 'space-x-2',
  },
}

export const animationVariants = {
  fadeIn: 'animate-in fade-in-0 duration-300',
  fadeOut: 'animate-out fade-out-0 duration-300',
  slideIn: 'animate-in fade-in-0 duration-300',
  slideOut: 'animate-out fade-out-0 duration-300',
  scaleIn: 'animate-in fade-in-0 duration-300',
  scaleOut: 'animate-out fade-out-0 duration-300',
}
